import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { find, isEmpty, uniqBy, uniqueId } from 'lodash';
import {
  getFlowAdvanced,
  getFlowInitVariables,
  getFlowSteps,
  queryFlowList,
} from '@/services/execFlowService';
import { getFlowGroupList } from '@/services/templateService';
import { stencilTypeOptions } from '@/utils/constant';
import { convertDetailToProps } from '@/pages/Maintenance/Template/Variables/components/VarInput';

const initialState = {
  currentCluster: null,
  currentEnv: null,
  steps: [],
  flowList: [],
  flowListFetching: false,
  hostsList: [],
  groupHostsList: [],
  cmpVariables: [],
  ipVariables: [],
  advancedSteps: [],
  versionList: [],
  flowGroup: [],
  loading: false,
  quickDeployFlow: {},
  fileInfoList: [],
};

export const fetchFlowList = createAsyncThunk('execFlow/fetchFlowList', async (params) => {
  const response = await queryFlowList(params);
  return response.data.items;
});

export const fetchFlowSteps = createAsyncThunk('execFlow/fetchFlowSteps', async (params) => {
  const {
    data: { opsExecLogStepVO },
  } = await getFlowSteps(params);
  return opsExecLogStepVO.map((item) => ({
    ...item,
    id: uniqueId(),
    prettyText: `${find(stencilTypeOptions, { value: item.stencilType }).label}：${item.stepName}`,
  }));
});

export const fetchFlowGroups = createAsyncThunk('execFlow/fetchFlowGroups', async (flowId) => {
  const { data } = await getFlowGroupList({ flowBaseId: flowId });
  return data;
});

export const fetchFlowInitVariables = createAsyncThunk(
  'execFlow/fetchFlowInitVariables',
  async (params) => {
    const response = await getFlowInitVariables(params);
    return response.data;
  },
);

export const fetchFlowAdvanced = createAsyncThunk('execFlow/fetchFlowAdvanced', async (params) => {
  const response = await getFlowAdvanced(params);
  return response.data;
});

/* 
  快速部署用flow，根据tag区分
  fastDeployType=machine -- 物理/虚拟机
  fastDeployType=container -- 容器
*/
export const fetchQuickDeployFlow = createAsyncThunk('execFlow/fetchQuickDeployFlow', async () => {
  const response = await queryFlowList({ startIndex: 1, pageSize: 99999 });
  return response.data.items;
});

export const execFlowSlice = createSlice({
  name: 'execFlow',
  initialState,
  reducers: {
    setCurrentCluster: (state, action) => {
      state.currentCluster = action.payload;
    },
    setCurrentEnv: (state, action) => {
      state.currentEnv = action.payload;
    },
    setUploadFiles: (state, action) => {
      state.fileInfoList = action.payload;
    },
    resetFlow: () => {
      return initialState;
    },
    addToHostList: (state, action) => {
      state.hostsList = [action.payload, ...state.hostsList];
    },
    modifyHosts: (state, action) => {
      const { currentStep, hosts } = action.payload;
      // 指向当前步骤的host对象
      const current = state.hostsList.find((item) => item.stepId === currentStep.stepId);
      // 还没添加主机
      if (!current) {
        state.hostsList.push({
          stepId: currentStep.stepId,
          id: currentStep.id,
          hosts,
        });
      } else {
        current.hosts = hosts;
      }
    },
    modifyGroupHosts: (state, action) => {
      const { currentGroup, hosts } = action.payload;

      // 指向当前步骤的host对象
      const current = state.groupHostsList.find((item) => item.groupId === currentGroup.id);
      // 还没添加主机
      if (!current) {
        state.groupHostsList.push({
          groupId: currentGroup.id,
          hosts,
        });
      } else {
        current.hosts = hosts;
      }
    },

    removeHost: (state, action) => {
      const { currentStep, hostId } = action.payload;
      // 指向当前步骤的host对象
      const current = state.hostsList.find((item) => item.stepId === currentStep.stepId);
      current.hosts = current.hosts.filter((item) => item.id !== hostId);
    },
    removeGroupHost: (state, action) => {
      const { currentGroup, hostId } = action.payload;
      // 指向当前步骤的host对象
      const current = state.groupHostsList.find((item) => item.groupId === currentGroup.id);
      current.hosts = current.hosts.filter((item) => item.id !== hostId);
    },
    generateHosts: (state) => {
      // 生成具体节点的主机
      const { steps, groupHostsList } = state;
      const list = []; // 放实际主机关系
      steps.forEach((item) => {
        const foundGroup = groupHostsList.find((groupItem) => groupItem.groupId === item.groupId);

        // 找到配置过机器的步骤组
        if (!isEmpty(foundGroup)) {
          // 根据规则置入，groupRule=2 只在第一台主机执行
          list.push({
            stepId: item.stepId,
            id: item.id,
            hosts: item.groupRule === 2 ? foundGroup.hosts.slice(0, 1) : foundGroup.hosts,
          });
        }
      });
      // 单节点留下
      state.hostsList = uniqBy([...list, ...state.hostsList], 'id');
    },
    setCmpVariables: (state, action) => {
      state.cmpVariables = action.payload;
    },
    setIpVariables: (state, action) => {
      state.ipVariables = action.payload;
    },
    setVersionList: (state, action) => {
      state.versionList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFlowList.pending, (state) => {
        state.flowListFetching = true;
      })
      .addCase(fetchFlowList.fulfilled, (state, action) => {
        state.flowListFetching = false;
        state.flowList = action.payload;
      })
      .addCase(fetchFlowList.rejected, (state) => {
        state.flowListFetching = false;
      })
      .addCase(fetchFlowSteps.pending, (state) => {
        state.steps = [];
        state.hostsList = [];
      })
      .addCase(fetchFlowSteps.fulfilled, (state, action) => {
        state.steps = action.payload;
      })
      .addCase(fetchFlowGroups.fulfilled, (state, action) => {
        state.flowGroup = action.payload;
      })
      .addCase(fetchFlowInitVariables.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFlowInitVariables.fulfilled, (state, action) => {
        const { opsFlowStepVariableVOList } = action.payload;
        const allVariables = [];
        // 所有主机
        let hosts = [];
        state.hostsList.forEach((item) => {
          hosts = [...hosts, ...item.hosts];
        });
        hosts = uniqBy(hosts, 'id');

        // 根据主机信息置入变量、id信息、防重复获取覆盖已有变量参数
        opsFlowStepVariableVOList.forEach((item) => {
          const { initFlowVariableVOList, stepId, scriptId, ...others } = item;
          initFlowVariableVOList.forEach((subItem) => {
            // 发现已经存在（可能修改过，比如下一步、返回上一步）的变量，则用修改过的
            let found;
            // 组件变量
            if (subItem.variableLevel === 0) {
              found = state.cmpVariables.find(
                (varItem) =>
                  varItem.key === subItem.key &&
                  varItem.stepId === stepId &&
                  varItem.scriptId === scriptId,
              );
            }
            // IP级
            if (subItem.variableLevel === 1) {
              found = state.ipVariables.find(
                (varItem) =>
                  varItem.hostId === subItem.hostId &&
                  varItem.key === subItem.key &&
                  varItem.stepId === stepId &&
                  varItem.scriptId === scriptId,
              );
            }
            // 标记选中;
            const tagSelect = subItem.variableType === 0 || subItem.notEqual;
            const variable = {
              ...subItem,
              ...others,
              stepId,
              scriptId,
              inputProps: convertDetailToProps(subItem.showDetail),
              host: hosts.find((host) => host.id === subItem.hostId),
              tagSelect,
              canDelete: !tagSelect, // 是否允许手动删除
              ...found,
              id: uniqueId(), // 不作为逻辑使用，当前仅用作key
              originValue: subItem.defaultValue, // 备份默认值，方便删除时恢复,拿最新
            };

            allVariables.push(variable);
          });
        });

        state.cmpVariables = allVariables.filter((item) => item.variableLevel === 0);
        state.ipVariables = allVariables.filter((item) => item.variableLevel === 1);

        state.loading = false;
      })
      .addCase(fetchFlowInitVariables.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchFlowAdvanced.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFlowAdvanced.fulfilled, (state, action) => {
        const versionList = [];
        state.advancedSteps = action.payload.map((item) => {
          if (item.stencilType === 1 && !isEmpty(item.productLibList)) {
            versionList.push(item.productLibList[0]);
          }
          return {
            ...item,
            id: uniqueId(),
            prettyText: `${find(stencilTypeOptions, { value: item.stencilType }).label}：${
              item.stepName
            }`,
          };
        });
        state.versionList = versionList;
        state.loading = false;
      })
      .addCase(fetchFlowAdvanced.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchQuickDeployFlow.fulfilled, (state, action) => {
        const resultList = action.payload;
        // 暂不区分大小写
        state.quickDeployFlow = {
          host: resultList.find(
            (item) => item.tag?.toLowerCase() === 'fastDeployType=machine'.toLowerCase(),
          ),
          k8s: resultList.find(
            (item) => item.tag?.toLowerCase() === 'fastDeployType=container'.toLowerCase(),
          ),
        };
      });
  },
});

export const {
  setCurrentCluster,
  setCurrentEnv,
  resetFlow,
  modifyGroupHosts,
  generateHosts,
  removeHost,
  setCmpVariables,
  setIpVariables,
  setVersionList,
  modifyHosts,
  removeGroupHost,
  addToHostList,
  setUploadFiles,
} = execFlowSlice.actions;
export default execFlowSlice.reducer;
